// src/services/notifications.js
import { db } from "./firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  getDocs,
  orderBy,
  limit
} from "firebase/firestore";

// Browser notification
export const showNotification = (title, body) => {
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification(title, { body });
  } else if ("Notification" in window) {
    Notification.requestPermission();
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if ("Notification" in window) {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }
  return false;
};

// Create a scheduled notification for return reminders
export const scheduleReturnReminder = async (requestData) => {
  try {
    const now = new Date();
    const returnDateTime = new Date(`${requestData.borrowingDate}T${requestData.returnTime}`);
    const reminderTime = new Date(returnDateTime.getTime() - (60 * 60 * 1000)); // 1 hour before return

    console.log("Scheduling reminder for return:", returnDateTime, "reminder at:", reminderTime);

    // Only schedule if the reminder time is in the future
    if (reminderTime > now) {
      await addDoc(collection(db, "scheduledNotifications"), {
        type: "return_reminder",
        requestId: requestData.id,
        studentId: requestData.requesterId,
        studentEmail: requestData.userEmail,
        studentName: requestData.requesterName,
        assetName: requestData.assetName,
        assetId: requestData.assetId,
        returnDateTime: returnDateTime.toISOString(),
        reminderTime: reminderTime.toISOString(),
        scheduledFor: reminderTime,
        status: "pending",
        createdAt: serverTimestamp(),
        message: `Reminder: Please return ${requestData.assetName} by ${requestData.returnTime} on ${requestData.borrowingDate}.`
      });

      console.log("Return reminder scheduled for future date:", reminderTime);
    } else {
      console.log("Return reminder not scheduled - reminder time is in the past:", reminderTime);
    }
  } catch (error) {
    console.error("Error scheduling return reminder:", error);
  }
};

// Create overdue notification for admin
export const createOverdueNotification = async (requestData, overdueHours) => {
  try {
    await addDoc(collection(db, "adminNotifications"), {
      type: "overdue_asset",
      requestId: requestData.id,
      studentId: requestData.requesterId,
      studentEmail: requestData.userEmail,
      studentName: requestData.requesterName,
      studentFirstName: requestData.studentFirstName,
      studentMiddleName: requestData.studentMiddleName,
      studentSurname: requestData.studentSurname,
      studentLrn: requestData.studentLrn,
      studentGradeYearLevel: requestData.studentGradeYearLevel,
      studentContactNumber: requestData.studentContactNumber,
      assetName: requestData.assetName,
      assetId: requestData.assetId,
      borrowingDate: requestData.borrowingDate,
      returnTime: requestData.returnTime,
      overdueHours: overdueHours,
      status: "unread",
      createdAt: serverTimestamp(),
      message: `${requestData.assetName} is overdue by ${overdueHours} hours. Student: ${requestData.requesterName} (${requestData.studentLrn})`
    });

    console.log("Overdue notification created for admin");
  } catch (error) {
    console.error("Error creating overdue notification:", error);
  }
};

// Get all admin notifications
export const getAdminNotifications = (callback) => {
  const q = query(
    collection(db, "adminNotifications"),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(notifications);
  });
};

// Get all student notifications
export const getStudentNotifications = (studentId, callback) => {
  const q = query(
    collection(db, "scheduledNotifications"),
    where("studentId", "==", studentId)
  );

  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).sort((a, b) => {
      const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt;
      const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : b.createdAt;
      return new Date(bTime) - new Date(aTime);
    });
    callback(notifications);
  });
};

// Get technician notifications (maintenance tasks)
export const getTechnicianNotifications = (technicianId, callback) => {
  const q = query(
    collection(db, "maintenanceTasks"),
    where("assignedTechnician", "==", technicianId),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map(doc => {
      const task = doc.data();
      return {
        id: doc.id,
        type: "maintenance_task",
        message: `Maintenance task assigned: ${task.assetName || 'Asset'} - ${task.description || 'No description'}`,
        createdAt: task.createdAt,
        status: (task.status === 'completed' || task.notificationStatus === 'read') ? 'read' : 'unread',
        assetName: task.assetName,
        description: task.description,
        location: task.location,
        dueDate: task.dueDate,
        priority: task.priority
      };
    });
    callback(notifications);
  });
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId, collectionName = "adminNotifications") => {
  try {
    const updateData = { readAt: serverTimestamp() };

    if (collectionName === "maintenanceTasks") {
      updateData.notificationStatus = "read";
    } else {
      updateData.status = "read";
    }

    await updateDoc(doc(db, collectionName, notificationId), updateData);
  } catch (error) {
    console.error("Error marking notification as read:", error);
  }
};

// Delete notification (hard delete)
export const deleteNotification = async (notificationId, collectionName = "adminNotifications") => {
  try {
    const docRef = doc(db, collectionName, notificationId);
    await deleteDoc(docRef);
    console.log(`Notification ${notificationId} hard deleted`);
  } catch (error) {
    console.error("Error deleting notification:", error);
  }
};

// Check for overdue assets and create notifications
export const checkOverdueAssets = async () => {
  try {
    const now = new Date();
    const requestsRef = collection(db, "requests");
    const q = query(
      requestsRef,
      where("status", "==", "approved"),
      where("type", "==", "borrow")
    );

    const snapshot = await getDocs(q);
    const overdueRequests = [];

    snapshot.forEach(doc => {
      const request = { id: doc.id, ...doc.data() };
      if (request.borrowingDate && request.returnTime) {
        const returnDateTime = new Date(`${request.borrowingDate}T${request.returnTime}`);
        const hoursOverdue = (now - returnDateTime) / (1000 * 60 * 60);

        if (hoursOverdue > 0) {
          overdueRequests.push({ ...request, hoursOverdue });
        }
      }
    });

    // Create notifications for overdue assets
    for (const request of overdueRequests) {
      // Check if notification already exists
      const existingNotificationQuery = query(
        collection(db, "adminNotifications"),
        where("requestId", "==", request.id),
        where("type", "==", "overdue_asset")
      );

      const existingSnapshot = await getDocs(existingNotificationQuery);
      if (existingSnapshot.empty) {
        await createOverdueNotification(request, Math.floor(request.hoursOverdue));
      }
    }

    return overdueRequests;
  } catch (error) {
    console.error("Error checking overdue assets:", error);
    return [];
  }
};

// Create notification for borrow request (admin)
export const createBorrowRequestNotification = async (requestData) => {
  try {
    await addDoc(collection(db, "adminNotifications"), {
      type: "borrow_request",
      requestId: requestData.id,
      studentId: requestData.requesterId,
      studentEmail: requestData.userEmail,
      studentName: requestData.requesterName,
      firstName: requestData.firstName || requestData.studentFirstName || "",
      middleName: requestData.middleName || requestData.studentMiddleName || "",
      surname: requestData.surname || requestData.studentSurname || "",
      lrn: requestData.lrn || requestData.studentLrn || "",
      gradeYearLevel: requestData.gradeYearLevel || requestData.studentGradeYearLevel || "",
      contactNumber: requestData.contactNumber || requestData.studentContactNumber || "",
      assetName: requestData.assetName,
      assetId: requestData.assetId,
      borrowingDate: requestData.borrowingDate,
      returnTime: requestData.returnTime,
      status: "unread",
      createdAt: serverTimestamp(),
      message: `${requestData.requesterName || requestData.requesterName} ${requestData.gradeYearLevel || requestData.studentGradeYearLevel ? `(${requestData.gradeYearLevel || requestData.studentGradeYearLevel})` : ''} has requested to borrow "${requestData.assetName}". Please review and approve.`
    });

    console.log("Borrow request notification created for admin");
  } catch (error) {
    console.error("Error creating borrow request notification:", error);
  }
};

// Create notification for asset return (admin)
export const createAssetReturnNotification = async (returnData) => {
  try {
    await addDoc(collection(db, "adminNotifications"), {
      type: "asset_return",
      requestId: returnData.id,
      studentId: returnData.requesterId,
      studentEmail: returnData.userEmail,
      studentName: returnData.requesterName,
      firstName: returnData.firstName,
      middleName: returnData.middleName,
      surname: returnData.surname,
      gradeYearLevel: returnData.gradeYearLevel,
      contactNumber: returnData.contactNumber,
      assetName: returnData.assetName,
      assetId: returnData.assetId,
      borrowingDate: returnData.borrowingDate,
      returnTime: returnData.returnTime,
      returnedAt: new Date().toISOString(),
      status: "unread",
      createdAt: serverTimestamp(),
      message: `${returnData.requesterName} has returned the asset "${returnData.assetName}".`
    });

    console.log("Asset return notification created for admin");
  } catch (error) {
    console.error("Error creating asset return notification:", error);
  }
};

// Create notification for request approval (student)
export const createRequestApprovalNotification = async (requestData) => {
  try {
    console.log("Creating approval notification for:", requestData.requesterId, requestData.assetName);

    const notificationData = {
      type: "request_approved",
      requestId: requestData.id,
      studentId: requestData.requesterId,
      studentEmail: requestData.userEmail,
      studentName: requestData.requesterName,
      assetName: requestData.assetName,
      assetId: requestData.assetId,
      borrowingDate: requestData.borrowingDate,
      returnTime: requestData.returnTime,
      status: "unread",
      createdAt: serverTimestamp(),
      scheduledFor: serverTimestamp(), // Include for proper ordering
      message: `Your request to ${requestData.type === 'maintenance' ? 'maintain' : requestData.type} "${requestData.assetName}" has been approved.`
    };

    await addDoc(collection(db, "scheduledNotifications"), notificationData);

    console.log("Request approval notification created successfully for user:", requestData.requesterId);
  } catch (error) {
    console.error("Error creating request approval notification:", error);
  }
};

// Create past approval notifications for all approved requests without notifications
export const createPastApprovalNotifications = async () => {
  try {
    // Get all approved borrow requests
    const approvedRequestsQuery = query(
      collection(db, "requests"),
      where("status", "==", "approved"),
      where("type", "==", "borrow")
    );

    const approvedRequestsSnap = await getDocs(approvedRequestsQuery);

    for (const requestDoc of approvedRequestsSnap.docs) {
      const requestData = { id: requestDoc.id, ...requestDoc.data() };

      // Check if approval notification already exists for this request
      const existingNotificationQuery = query(
        collection(db, "scheduledNotifications"),
        where("requestId", "==", requestData.id),
        where("type", "==", "request_approved")
      );

      const existingSnap = await getDocs(existingNotificationQuery);

      if (existingSnap.empty) {
        // Create the notification
        await addDoc(collection(db, "scheduledNotifications"), {
          type: "request_approved",
          requestId: requestData.id,
          studentId: requestData.requesterId,
          studentEmail: requestData.userEmail,
          studentName: requestData.requesterName,
          assetName: requestData.assetName,
          assetId: requestData.assetId,
          borrowingDate: requestData.borrowingDate,
          returnTime: requestData.returnTime,
          status: "unread",
          createdAt: requestData.lastUpdated || requestData.createdAt, // Use the approval time if available
          scheduledFor: requestData.lastUpdated || requestData.createdAt, // For consistent ordering
          message: `Your request to borrow "${requestData.assetName}" has been approved. You can now pick it up from the admin office.`
        });

        console.log(`Created past approval notification for request ${requestData.id}`);
      }
    }

    console.log("Past approval notifications creation completed");
  } catch (error) {
    console.error("Error creating past approval notifications:", error);
  }
};

// Send scheduled notifications (to be called periodically)
export const sendScheduledNotifications = async () => {
  try {
    const now = new Date();
    const q = query(
      collection(db, "scheduledNotifications"),
      where("status", "==", "pending")
    );

    const snapshot = await getDocs(q);

    snapshot.forEach(async (doc) => {
      const notification = { id: doc.id, ...doc.data() };
      const scheduledTime = new Date(notification.scheduledFor);

      if (now >= scheduledTime) {
        // Send notification to student
        showNotification("Asset Return Reminder", notification.message);

        // Mark as sent
        await updateDoc(doc(db, "scheduledNotifications", notification.id), {
          status: "sent",
          sentAt: serverTimestamp()
        });
      }
    });
  } catch (error) {
    console.error("Error sending scheduled notifications:", error);
  }
};
